import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import LoginModal from '@/components/LoginModal'
import { 
  ArrowRight,
  TrendingUp,
  Users,
  Star,
  ShoppingCart,
  CreditCard,
  GraduationCap,
  Car,
  Heart,
  Globe,
  Zap,
  Shield
} from 'lucide-react'

// 行业分类
const industryCategories = {
  ecommerce: {
    title: '电商零售',
    description: '为电商平台提供完整的短信解决方案',
    icon: ShoppingCart,
    color: 'text-orange-600'
  },
  finance: {
    title: '金融服务',
    description: '保障金融交易安全的短信验证',
    icon: CreditCard,
    color: 'text-blue-600'
  },
  education: {
    title: '在线教育',
    description: '教育行业的通知和营销短信',
    icon: GraduationCap,
    color: 'text-green-600'
  },
  logistics: {
    title: '物流运输',
    description: '物流状态通知和客户服务',
    icon: Car,
    color: 'text-purple-600'
  },
  healthcare: {
    title: '医疗健康',
    description: '医疗预约和健康提醒服务',
    icon: Heart,
    color: 'text-red-600'
  },
  travel: {
    title: '旅游出行',
    description: '旅游预订和行程通知',
    icon: Globe,
    color: 'text-cyan-600'
  }
}

// 客户案例
const caseStudies = [
  {
    id: 1,
    company: '某大型电商平台',
    industry: 'ecommerce',
    logo: '🛒',
    description: '国内领先的综合性电商平台，日活跃用户超过1000万',
    challenge: '随着业务快速增长，原有的短信服务无法满足高并发需求，经常出现发送延迟和失败的情况，影响用户体验。',
    solution: '接入我们的短信云平台，使用智能路由和负载均衡技术，实现高并发短信发送。',
    results: [
      { metric: '发送成功率', value: '99.8%', improvement: '+15%' },
      { metric: '平均延迟', value: '2.3秒', improvement: '-60%' },
      { metric: '日发送量', value: '500万+', improvement: '+200%' },
      { metric: '用户满意度', value: '98%', improvement: '+25%' }
    ],
    testimonial: {
      content: '接入SMS云平台后，我们的验证码到达率从85%提升到99.8%，用户投诉大幅减少，业务增长更加稳定。',
      author: '技术总监 张先生',
      position: '某大型电商平台'
    },
    features: ['智能路由', '高并发处理', '实时监控', '故障转移']
  },
  {
    id: 2,
    company: '某金融科技公司',
    industry: 'finance',
    logo: '🏦',
    description: '专注于移动支付和数字银行服务的金融科技公司',
    challenge: '金融行业对安全性要求极高，需要稳定可靠的短信验证服务，同时要满足监管合规要求。',
    solution: '采用我们的企业级短信服务，配备多重安全防护和合规认证，确保交易安全。',
    results: [
      { metric: '安全事件', value: '0次', improvement: '-100%' },
      { metric: '验证成功率', value: '99.9%', improvement: '+12%' },
      { metric: '响应时间', value: '1.8秒', improvement: '-45%' },
      { metric: '合规认证', value: '100%', improvement: '通过' }
    ],
    testimonial: {
      content: '平台的安全性和稳定性让我们很放心，API接入也非常简单，大大提升了我们的开发效率。',
      author: '产品经理 李女士',
      position: '某金融科技公司'
    },
    features: ['企业级安全', '合规认证', 'API集成', '实时监控']
  },
  {
    id: 3,
    company: '某在线教育平台',
    industry: 'education',
    logo: '🎓',
    description: '国内知名的在线教育平台，服务学员超过500万',
    challenge: '需要向大量学员发送课程提醒、作业通知等消息，传统短信服务成本高且效率低。',
    solution: '使用我们的批量短信服务和模板管理功能，实现自动化消息推送。',
    results: [
      { metric: '发送成本', value: '降低30%', improvement: '-30%' },
      { metric: '发送效率', value: '提升50%', improvement: '+50%' },
      { metric: '学员参与度', value: '85%', improvement: '+20%' },
      { metric: '运营效率', value: '提升40%', improvement: '+40%' }
    ],
    testimonial: {
      content: '成本降低了30%，发送效率提升了50%，学员的课程参与度也明显提高，非常满意。',
      author: 'CTO 王先生',
      position: '某在线教育平台'
    },
    features: ['批量发送', '模板管理', '成本优化', '自动化推送']
  },
  {
    id: 4,
    company: '某物流公司',
    industry: 'logistics',
    logo: '🚚',
    description: '全国性物流运输公司，日处理包裹超过100万件',
    challenge: '需要实时向客户推送物流状态更新，传统方式无法满足大规模、实时性的需求。',
    solution: '集成我们的实时短信推送服务，结合物流系统实现自动化状态通知。',
    results: [
      { metric: '通知及时性', value: '99.5%', improvement: '+35%' },
      { metric: '客户满意度', value: '92%', improvement: '+28%' },
      { metric: '客服咨询', value: '减少40%', improvement: '-40%' },
      { metric: '运营成本', value: '降低25%', improvement: '-25%' }
    ],
    testimonial: {
      content: '客户能够实时了解包裹状态，大大减少了客服咨询量，提升了客户满意度。',
      author: '运营总监 陈女士',
      position: '某物流公司'
    },
    features: ['实时推送', '状态跟踪', '自动化通知', '成本控制']
  },
  {
    id: 5,
    company: '某医疗健康平台',
    industry: 'healthcare',
    logo: '🏥',
    description: '专业的医疗健康服务平台，服务用户超过200万',
    challenge: '需要向用户发送预约提醒、健康检查通知等敏感信息，对安全性和可靠性要求极高。',
    solution: '采用我们的医疗级短信服务，配备端到端加密和HIPAA合规认证。',
    results: [
      { metric: '数据安全', value: '100%', improvement: '零泄露' },
      { metric: '预约到诊率', value: '88%', improvement: '+22%' },
      { metric: '用户满意度', value: '95%', improvement: '+18%' },
      { metric: '运营效率', value: '提升35%', improvement: '+35%' }
    ],
    testimonial: {
      content: '医疗数据的安全性是我们的首要考虑，SMS云平台的安全保障让我们非常放心。',
      author: '技术负责人 刘先生',
      position: '某医疗健康平台'
    },
    features: ['医疗级安全', 'HIPAA合规', '端到端加密', '隐私保护']
  },
  {
    id: 6,
    company: '某旅游平台',
    industry: 'travel',
    logo: '✈️',
    description: '综合性旅游服务平台，年服务用户超过1000万',
    challenge: '旅游行业季节性明显，需要灵活应对流量波动，同时要支持国际短信发送。',
    solution: '使用我们的弹性短信服务和国际短信功能，实现全球覆盖和弹性扩容。',
    results: [
      { metric: '国际覆盖', value: '200+国家', improvement: '全球覆盖' },
      { metric: '弹性扩容', value: '10倍', improvement: '自动扩容' },
      { metric: '发送成功率', value: '99.7%', improvement: '+20%' },
      { metric: '用户留存', value: '78%', improvement: '+15%' }
    ],
    testimonial: {
      content: '国际短信功能让我们能够服务全球用户，弹性扩容完美应对了旅游旺季的流量高峰。',
      author: '产品总监 赵女士',
      position: '某旅游平台'
    },
    features: ['国际短信', '弹性扩容', '全球覆盖', '智能路由']
  }
]

// 成功指标
const successMetrics = [
  {
    icon: TrendingUp,
    title: '业务增长',
    description: '平均帮助客户提升30%的业务效率',
    value: '30%'
  },
  {
    icon: Zap,
    title: '性能提升',
    description: '发送成功率平均提升到99.8%',
    value: '99.8%'
  },
  {
    icon: Shield,
    title: '安全保障',
    description: '零安全事故，100%数据安全',
    value: '100%'
  },
  {
    icon: Users,
    title: '客户满意',
    description: '客户满意度平均达到95%',
    value: '95%'
  }
]

export default function CaseStudiesPage() {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [activeIndustry, setActiveIndustry] = useState('all')

  const filteredCases = activeIndustry === 'all' 
    ? caseStudies 
    : caseStudies.filter(caseStudy => caseStudy.industry === activeIndustry)

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                🏆 成功案例
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                客户
                <span className="text-blue-600">成功案例</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                看看我们的客户如何通过SMS云平台实现业务增长，提升用户体验
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-3"
                onClick={() => setShowLoginModal(true)}
              >
                开始您的成功故事
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8 py-3" asChild>
                <Link to="/features">
                  查看功能
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 成功指标 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            我们的成功指标
          </h2>
          <p className="text-xl text-gray-600">
            数据说话，见证我们的服务价值
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {successMetrics.map((metric, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mx-auto w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <metric.icon className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-blue-600">{metric.value}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardTitle className="text-lg mb-2">{metric.title}</CardTitle>
                <CardDescription>{metric.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* 行业分类 */}
      <section className="bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              服务行业
            </h2>
            <p className="text-xl text-gray-600">
              覆盖各行各业，满足不同业务需求
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {Object.entries(industryCategories).map(([key, industry]) => (
              <Card 
                key={key} 
                className={`text-center cursor-pointer hover:shadow-lg transition-all ${
                  activeIndustry === key ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setActiveIndustry(key)}
              >
                <CardHeader>
                  <div className="mx-auto w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                    <industry.icon className={`h-6 w-6 ${industry.color}`} />
                  </div>
                  <CardTitle className="text-sm">{industry.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-xs">{industry.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 客户案例 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            客户案例
          </h2>
          <p className="text-xl text-gray-600">
            真实案例，见证我们的服务价值
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredCases.map((caseStudy) => (
            <Card key={caseStudy.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">{caseStudy.logo}</div>
                  <div className="flex-1">
                    <CardTitle className="text-xl">{caseStudy.company}</CardTitle>
                    <CardDescription className="text-base">
                      {caseStudy.description}
                    </CardDescription>
                    <Badge className="mt-2">
                      {industryCategories[caseStudy.industry as keyof typeof industryCategories].title}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-2">挑战</h4>
                  <p className="text-gray-600 text-sm">{caseStudy.challenge}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-lg mb-2">解决方案</h4>
                  <p className="text-gray-600 text-sm">{caseStudy.solution}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-lg mb-3">成果</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {caseStudy.results.map((result, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600">{result.metric}</div>
                        <div className="font-semibold text-lg">{result.value}</div>
                        <div className="text-xs text-green-600">{result.improvement}</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <blockquote className="text-gray-600 italic">
                    "{caseStudy.testimonial.content}"
                  </blockquote>
                  <div className="mt-2 text-sm text-gray-500">
                    — {caseStudy.testimonial.author}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {caseStudy.features.map((feature, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* 客户评价 */}
      <section className="bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold">
              客户评价
            </h2>
            <p className="text-xl text-gray-300">
              听听我们的客户怎么说
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                content: 'SMS云平台帮助我们实现了99.8%的发送成功率，用户体验大幅提升。',
                author: '张先生',
                company: '某电商平台',
                rating: 5
              },
              {
                content: '安全性和稳定性让我们很放心，API接入也非常简单。',
                author: '李女士',
                company: '某金融科技',
                rating: 5
              },
              {
                content: '成本降低了30%，发送效率提升了50%，非常满意。',
                author: '王先生',
                company: '某教育机构',
                rating: 5
              }
            ].map((testimonial, index) => (
              <Card key={index} className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <blockquote className="text-gray-300 mb-4">
                    "{testimonial.content}"
                  </blockquote>
                  <div className="text-sm">
                    <div className="font-semibold text-white">{testimonial.author}</div>
                    <div className="text-gray-400">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 开始您的成功故事 */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              开始您的成功故事
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              加入我们的客户行列，体验专业的短信服务，实现业务增长
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-3"
                onClick={() => setShowLoginModal(true)}
              >
                立即开始
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-3 border-white text-white hover:bg-white hover:text-blue-600" asChild>
                <Link to="/pricing">
                  查看价格
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 登录 Modal */}
      <LoginModal 
        open={showLoginModal} 
        onOpenChange={setShowLoginModal} 
      />
    </div>
  )
}
